import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/lib/seo";

const PRINCIPLES = [
  {
    title: "Candour",
    body: "Plain speaking from the first conversation. If we are not the right people to help, we will say so.",
  },
  {
    title: "Craft",
    body: "Careful, tailored work. Every recommendation is shaped around your business and its circumstances, never taken off the shelf.",
  },
  {
    title: "Commitment",
    body: "Support that continues as plans are put into practice — not advice that ends with the first discussion.",
  },
];

export default function About() {
  usePageMeta(
    "About — W Group Consulting",
    "W Group provides general business consultancy, management consultancy and advisory support, including help exploring opportunities in different jurisdictions.",
    "/about"
  );

  return (
    <>
      <PageHero
        eyebrow="About the firm"
        titleLines={["Practical support,", "shaped around you."]}
        description="W Group provides general business consultancy, management consultancy and advisory support, including helping businesses explore opportunities in different jurisdictions."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="about-story">
        <Reveal>
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl leading-tight text-ink sm:text-4xl">
              Straightforward advice, shaped around each business.
            </h2>
            <p className="mt-8 text-base leading-relaxed text-gray-600">
              We help business owners and management teams explore their
              options, plan their next steps and move their businesses
              forward. Some clients come to us to improve the way an existing
              business runs; others are weighing up growth or a move into a
              new market. The starting point is always the same: understanding
              your position and what you want to achieve.
            </p>
            <p className="mt-5 text-base leading-relaxed text-gray-600">
              We keep the work practical — open discussion, an honest view of
              the options, and support that carries through to implementation.
              Where a matter calls for specialist input, we will tell you and
              help you work with the right professional advisers.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="about-principles">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">Principles</p>
            <h2 className="max-w-2xl font-display text-4xl leading-tight text-ink sm:text-5xl">
              Three words we are prepared to be judged by.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-ink/10 md:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="flex h-full flex-col bg-white p-8 lg:p-10" data-testid={`principle-${p.title.toLowerCase()}`}>
                  <span className="font-display text-4xl italic text-gold">{p.title.charAt(0)}</span>
                  <h3 className="mt-8 font-display text-2xl text-ink">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-gray-500">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
